import { useState, useEffect, useMemo } from 'react'
import { useAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Stack,
  Typography,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Alert,
  Skeleton,
  IconButton,
  Tooltip,
} from '@mui/material'
import {
  People as PeopleIcon,
  Send as SendIcon,
  Groups as GroupsIcon,
  Close as CloseIcon,
  PendingActions as PendingActionsIcon,
  Schedule as ScheduleIcon,
  AssignmentLate as AssignmentLateIcon,
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material'
import { userAtom } from '../state/auth'
import { colors } from '../theme'
import { dashboardApi, DashboardData, Task, jobOpportunitiesApi } from '../services/api'
import { APPLY_DECISION_COLORS } from '../state/jobOpportunities'
import { workflowStageDefinitionsAtom } from '../state/clients'
import ErrorState from '../components/common/ErrorState'
import { isAdminOrManager, canSeeLeadPipeline } from '../utils/roles'
import PendingAssignmentsBanner from '../components/PendingAssignmentsBanner'
import PageHelpButton from '../components/PageHelpButton'
import { ensureHttpProtocol } from '../utils/validation'

interface StatCardProps {
  title: string
  value: string | number
  icon: React.ReactNode
  color: string
  loading?: boolean
}

function StatCard({ title, value, icon, color, loading }: StatCardProps) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 2, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
            mb: { xs: 1, sm: 2 },
          }}
        >
          <Typography
            color="text.secondary"
            variant="subtitle2"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minWidth: 0 }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              width: { xs: 32, sm: 40 },
              height: { xs: 32, sm: 40 },
              flexShrink: 0,
              borderRadius: 2,
              backgroundColor: color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            {icon}
          </Box>
        </Box>
        {loading ? (
          <Skeleton variant="text" width={60} height={40} />
        ) : (
          <Typography variant="h4" sx={{ fontWeight: 600, mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            {value}
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * MWA-341 — one of the three counts that answer "what is waiting on me".
 *
 * Separate from StatCard on purpose. A stat is a fact about the book; this is
 * a queue with your name on it, so it carries the action's own colour, the
 * sentence explaining what the number means, and a route to the list it counts.
 * Merging the two would leave the reader deciding which numbers are chores.
 *
 * Zero is a real state and reads calmly: no alarm colour, no tag, and a line
 * saying so. A dashboard that shouts at you when nothing is wrong gets ignored
 * when something is.
 */
function NeedsAttentionCard({
  label,
  value,
  hint,
  zeroHint,
  tone,
  icon,
  onOpen,
  loading,
}: {
  label: string
  value: number
  hint: string
  zeroHint: string
  tone: 'warning' | 'error'
  icon: React.ReactNode
  onOpen: () => void
  loading?: boolean
}) {
  const active = !loading && value > 0

  return (
    <Card sx={{ height: '100%' }}>
      <CardActionArea onClick={onOpen} sx={{ height: '100%', p: 2, alignItems: 'stretch' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ display: 'flex', color: active ? `${tone}.dark` : 'text.disabled', '& svg': { fontSize: 18 } }}>
            {icon}
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', flexGrow: 1, minWidth: 0 }}>
            {label}
          </Typography>
          <ChevronRightIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
        </Stack>

        {loading ? (
          <Skeleton variant="text" width={72} height={44} sx={{ mt: 0.5 }} />
        ) : (
          <Stack direction="row" alignItems="baseline" spacing={1.25} sx={{ mt: 0.75 }}>
            <Typography
              component="span"
              sx={{
                fontSize: { xs: '1.75rem', sm: '1.875rem' },
                lineHeight: 1.1,
                fontWeight: 600,
                letterSpacing: '-0.02em',
                fontVariantNumeric: 'tabular-nums',
                color: active ? `${tone}.dark` : 'text.primary',
              }}
            >
              {value}
            </Typography>
            {active && (
              <Chip
                label={tone === 'error' ? 'past due' : 'waiting'}
                size="small"
                color={tone}
                variant="outlined"
                sx={{ height: 20, fontSize: '0.6875rem' }}
              />
            )}
          </Stack>
        )}

        <Typography variant="caption" sx={{ display: 'block', mt: 0.75, color: 'text.secondary', fontWeight: 400 }}>
          {loading ? ' ' : active ? hint : zeroHint}
        </Typography>
      </CardActionArea>
    </Card>
  )
}

// One island. Carries the heading, the loading spinner and the empty state so
// no island can invent its own version of those three.
function DashboardPanel({
  title,
  loading,
  isEmpty,
  emptyMessage,
  children,
}: {
  title: string
  loading: boolean
  isEmpty: boolean
  emptyMessage: string
  children: React.ReactNode
}) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography
          variant="h6"
          component="h2"
          sx={{ mb: { xs: 1.5, sm: 2 }, fontSize: { xs: '1rem', sm: '1.25rem' } }}
        >
          {title}
        </Typography>
        {loading ? (
          // Rows in the panel's own shape rather than a centred spinner: the
          // spinner collapses the card to nothing, so every panel jumps to its
          // real height the moment data lands.
          <Stack spacing={1.5} sx={{ py: 0.5 }}>
            {['72%', '54%', '86%', '61%'].map((w, i) => (
              <Stack key={i} direction="row" alignItems="center" spacing={2}>
                <Skeleton variant="text" width={w} height={18} sx={{ flexGrow: 1 }} />
                <Skeleton variant="rounded" width={44} height={18} sx={{ borderRadius: '6px', flexShrink: 0 }} />
              </Stack>
            ))}
          </Stack>
        ) : isEmpty ? (
          <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
            {emptyMessage}
          </Typography>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  )
}

export default function Dashboard() {
  const [user] = useAtom(userAtom)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
  const [dismissingId, setDismissingId] = useState<string | null>(null)
  const [workflowStages] = useAtom(workflowStageDefinitionsAtom)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await dashboardApi.getData()
      setDashboardData(data)
    } catch (err) {
      console.error('Failed to load dashboard data:', err)
      setError('Failed to load dashboard data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleDismissApplication = async (id: string) => {
    setDismissingId(id)
    try {
      await jobOpportunitiesApi.update({ id, clearPending: true })
      await loadDashboardData()
    } catch (err) {
      console.error('Failed to dismiss pending application:', err)
      setError('Could not dismiss that application. Please try again.')
    } finally {
      setDismissingId(null)
    }
  }

  const startOfToday = (): number => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
  }

  const isOverdue = (dueDate?: string): boolean => {
    if (!dueDate) return false
    const d = new Date(dueDate)
    if (Number.isNaN(d.getTime())) return false
    d.setHours(0, 0, 0, 0)
    return d.getTime() < startOfToday()
  }

  const formatDueDate = (dueDate?: string): string => {
    if (!dueDate) return 'No due date'
    const date = new Date(dueDate)
    if (Number.isNaN(date.getTime())) return 'No due date'
    const today = new Date()
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)

    if (date.toDateString() === today.toDateString()) return 'Today'
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  const humanize = (value: string): string => value.replace(/_/g, ' ')

  const getStatusChip = (status: string) => {
    const statusColors: Record<string, 'warning' | 'info' | 'success'> = {
      pending: 'warning',
      in_progress: 'info',
      completed: 'success',
    }
    // An unmapped status must still render as a real chip rather than an
    // undefined colour, so a new task status can never come through blank.
    const color = statusColors[status]
    return (
      <Chip
        label={humanize(status)}
        size="small"
        color={color}
        variant={color ? 'filled' : 'outlined'}
        sx={{ textTransform: 'capitalize' }}
      />
    )
  }

  const stats = {
    activeLeads: dashboardData?.activeLeads ?? 0,
    activeClients: dashboardData?.activeClients ?? 0,
    applicationsThisWeek: dashboardData?.applicationsThisWeek ?? 0,
    clientsInterviewing: dashboardData?.clientsInterviewing ?? 0,
    leadsByStatus: dashboardData?.leadsByStatus ?? {},
    recentTasks: dashboardData?.recentTasks ?? [],
    pendingApplications: dashboardData?.pendingApplications ?? [],
    awaitingClientDecision: dashboardData?.awaitingClientDecision ?? 0,
    stalledApplications: dashboardData?.stalledApplications ?? 0,
    overdueTasks: dashboardData?.overdueTasks ?? 0,
    clientsByStage: dashboardData?.clientsByStage ?? {},
  }

  // The server sends the threshold it counted with, so this sentence and the
  // inactivity chips on the Job Search board always quote the same number.
  const staleDays = dashboardData?.staleThresholdDays ?? 14

  // Stage names are Stage Gates configuration. App.tsx loads the definitions
  // into this atom on boot, so resolving them here shows what an admin
  // actually called each stage rather than whatever the code last assumed.
  // Falls back to the raw id, which is ugly but never wrong.
  const stageLabels = useMemo(
    () => Object.fromEntries(workflowStages.map((s) => [s.stageId, s.label])),
    [workflowStages]
  )
  const stageRows = useMemo(() => {
    const entries = Object.entries(stats.clientsByStage)
    const order = new Map(workflowStages.map((s, i) => [s.stageId, i]))
    return entries
      .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
      .map(([stageId, count]) => ({
        stageId,
        count,
        // 'unassigned' is the server's sentinel for a client with no stage on
        // the record, not a stage anybody configured, so it gets a sentence
        // rather than being humanized into a lowercase word.
        label:
          stageId === 'unassigned'
            ? 'No stage set'
            : stageLabels[stageId] || humanize(stageId),
      }))
  }, [stats.clientsByStage, workflowStages, stageLabels])
  const stageMax = stageRows.reduce((m, r) => Math.max(m, r.count), 0)

  // The task list is assignment-scoped for ordinary staff but a full-table read
  // for admins and managers, so calling it "Your Tasks" for them was false.
  const tasksAreTeamWide = isAdminOrManager(user)
  const tasksTitle = tasksAreTeamWide ? 'Team Tasks' : 'Your Tasks'
  const showLeadPipeline = canSeeLeadPipeline(user)

  // A hard failure — the read never landed, so there is no dashboard to show
  // around an alert. The soft case (a dismiss that failed while data is on
  // screen) keeps the inline Alert below, because replacing a working page
  // with an error panel over one failed action would be the worse answer.
  if (!loading && error && !dashboardData) {
    return (
      <Box sx={{ maxWidth: 520, mx: 'auto', mt: 8 }}>
        <ErrorState
          title="Couldn't load your dashboard"
          description="Nothing was changed, and retrying is safe."
          detail={error}
          onRetry={loadDashboardData}
        />
      </Box>
    )
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: { xs: 0.5, sm: 1 } }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Welcome back, {user?.name || user?.email?.split('@')[0]}
        </Typography>
        <PageHelpButton articleId="getting-started-dashboard" />
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: { xs: 2, sm: 3 }, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
        Here's what's happening with your clients today
      </Typography>

      <PendingAssignmentsBanner />

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* MWA-341 — what is waiting on you, above what merely exists. Every
          count is derived from reads the dashboard already performed, and each
          one routes to the list it counts so the number can be reconciled. */}
      <Grid container spacing={{ xs: 2, sm: 2.5 }} sx={{ mb: { xs: 2, sm: 3 } }}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <NeedsAttentionCard
            label="Awaiting client decision"
            value={stats.awaitingClientDecision}
            hint="Asked, no answer yet. Nothing moves until they reply."
            zeroHint="No client is holding anything up."
            tone="warning"
            icon={<PendingActionsIcon />}
            loading={loading}
            onOpen={() => navigate('/opportunities')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <NeedsAttentionCard
            label={`No movement ${staleDays}+ days`}
            value={stats.stalledApplications}
            hint="Live applications nobody has touched in that time."
            zeroHint="Everything live has been touched recently."
            tone="warning"
            icon={<ScheduleIcon />}
            loading={loading}
            onOpen={() => navigate('/opportunities')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 12, lg: 4 }}>
          <NeedsAttentionCard
            label={tasksAreTeamWide ? 'Overdue tasks (team)' : 'Your overdue tasks'}
            value={stats.overdueTasks}
            hint="Due date passed and still not complete."
            zeroHint="Nothing is past its due date."
            tone="error"
            icon={<AssignmentLateIcon />}
            loading={loading}
            onOpen={() => navigate('/tasks')}
          />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <StatCard
            title="Active Leads"
            value={stats.activeLeads}
            icon={<PeopleIcon />}
            color={colors.primary}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <StatCard
            title="Active Clients"
            value={stats.activeClients}
            icon={<PeopleIcon />}
            color={colors.primaryLight}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <StatCard
            title="Applications This Week"
            value={stats.applicationsThisWeek}
            icon={<SendIcon />}
            color={colors.brandNavy}
            loading={loading}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 6, lg: 3 }}>
          <StatCard
            title="Clients Interviewing"
            value={stats.clientsInterviewing}
            icon={<GroupsIcon />}
            color={colors.secondary}
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={{ xs: 2, sm: 3 }}>
        <Grid size={{ xs: 12, md: showLeadPipeline ? 6 : 12 }}>
          <DashboardPanel
            title={tasksTitle}
            loading={loading}
            isEmpty={stats.recentTasks.length === 0}
            emptyMessage={tasksAreTeamWide ? 'No open tasks across the team' : 'No open tasks assigned to you'}
          >
            <List disablePadding>
              {stats.recentTasks.map((task: Task, index: number) => {
                const overdue = isOverdue(task.dueDate)
                const meta = [task.clientName, formatDueDate(task.dueDate)].filter(Boolean)
                return (
                  <ListItem
                    key={task.id}
                    divider={index < stats.recentTasks.length - 1}
                    disableGutters
                    sx={{ py: 1.25, alignItems: 'flex-start' }}
                    secondaryAction={getStatusChip(task.status)}
                  >
                    <Box sx={{ minWidth: 0, pr: 8 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {task.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          mt: 0.25,
                          color: overdue ? 'error.main' : 'text.secondary',
                          fontWeight: overdue ? 600 : 400,
                        }}
                      >
                        {overdue ? 'Overdue — ' : ''}
                        {meta.join(' · ')}
                      </Typography>
                    </Box>
                  </ListItem>
                )
              })}
            </List>
          </DashboardPanel>
        </Grid>

        {showLeadPipeline && (
          <Grid size={{ xs: 12, md: 6 }}>
            <DashboardPanel
              title="Leads by Status"
              loading={loading}
              isEmpty={Object.keys(stats.leadsByStatus).length === 0}
              emptyMessage="No leads yet"
            >
              <Stack spacing={1.5}>
                {Object.entries(stats.leadsByStatus).map(([status, count]) => (
                  <Stack
                    key={status}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}
                  >
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {humanize(status)}
                    </Typography>
                    <Chip
                      label={count + ' lead' + (count !== 1 ? 's' : '')}
                      size="small"
                      color={
                        status === 'new'
                          ? 'info'
                          : status === 'qualified'
                            ? 'success'
                            : status === 'not_qualified'
                              ? 'error'
                              : 'default'
                      }
                      sx={{ fontSize: { xs: '0.6875rem', sm: '0.8125rem' } }}
                    />
                  </Stack>
                ))}
              </Stack>
            </DashboardPanel>
          </Grid>
        )}

        {/* MWA-341 — where the book actually sits. Excludes complete, so this
            totals to the Active Clients tile rather than disagreeing with it.
            Labels come from Stage Gates, not from a constant in the code. */}
        <Grid size={{ xs: 12, md: 6 }}>
          <DashboardPanel
            title="Clients by Stage"
            loading={loading}
            isEmpty={stageRows.length === 0}
            emptyMessage={tasksAreTeamWide ? 'No active clients' : 'No active clients assigned to you'}
          >
            <Stack spacing={1.25}>
              {stageRows.map((row) => (
                <Stack key={row.stageId} direction="row" alignItems="center" spacing={1.5}>
                  <Typography
                    variant="body2"
                    sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {row.label}
                  </Typography>
                  <Box
                    sx={{
                      width: { xs: 64, sm: 92 },
                      height: 6,
                      flexShrink: 0,
                      borderRadius: 99,
                      bgcolor: 'action.hover',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        height: 6,
                        borderRadius: 99,
                        bgcolor: 'primary.main',
                        width: `${stageMax > 0 ? Math.round((row.count / stageMax) * 100) : 0}%`,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', width: 22, textAlign: 'right', flexShrink: 0 }}
                  >
                    {row.count}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </DashboardPanel>
        </Grid>

        {stats.pendingApplications.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <DashboardPanel title="Pending Applications" loading={false} isEmpty={false} emptyMessage="">
              <List disablePadding>
                {stats.pendingApplications.map((app, index) => (
                  <ListItem
                    key={app.id}
                    divider={index < stats.pendingApplications.length - 1}
                    disableGutters
                    disablePadding
                    secondaryAction={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {app.url && (
                          <Tooltip title="Open job posting">
                            <IconButton
                              size="small"
                              component="a"
                              href={ensureHttpProtocol(app.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              aria-label={'Open the job posting for ' + app.jobTitle + ' at ' + app.company}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Chip
                          label={app.applyDecision === 'apply_on_behalf' ? 'Apply for client' : 'Client applying'}
                          size="small"
                          sx={{
                            bgcolor: APPLY_DECISION_COLORS[app.applyDecision],
                            color: 'white',
                            fontSize: { xs: '0.6875rem', sm: '0.8125rem' },
                          }}
                        />
                        <Tooltip title="Dismiss">
                          <IconButton
                            size="small"
                            aria-label={'Dismiss ' + app.jobTitle + ' at ' + app.company + ' for ' + app.clientName}
                            disabled={dismissingId === app.id}
                            onClick={() => handleDismissApplication(app.id)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    {/* MWA-361 — opens this job, not the client record: the row
                        is a job to act on, and the modal carries the posting. */}
                    <ListItemButton
                      onClick={() =>
                        navigate('/clients/' + app.clientId + '/opportunities?job=' + encodeURIComponent(app.id))
                      }
                      sx={{ px: 0, py: 1.25, pr: app.url ? 29 : 24 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {app.clientName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                          {[app.jobTitle, app.company].filter(Boolean).join(' · ')}
                        </Typography>
                      </Box>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </DashboardPanel>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}
